import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { format } from 'date-fns';
import {
  ChartBar,
  Export,
  Funnel,
  X,
  Clock,
  Warning,
  CheckCircle,
  Spinner,
  FileText,
  Storefront,
  Buildings,
  ArrowsClockwise,
  FilePdf,
  Package,
  WarningOctagon,
  Receipt,
  Scales,
  CurrencyDollar,
  ClipboardText,
  Eye
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const ReportsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Role-based report access:
  // PM: All 8 reports
  // FC: Month End Inventory, Receiving, Variance, Spend by Dept (4 reports)
  // DH: Spend by Department (their dept only) (1 report)
  // Storekeeper: Inventory Count, Month End, Low Stock (3 reports)
  
  const isPM = ['purchasing_manager', 'system_admin'].includes(user?.role);
  const isFC = user?.role === 'financial_controller';
  const isDH = user?.role === 'department_head';
  const isStorekeeper = user?.role === 'storekeeper';
  
  // Report access based on role - as per v2.6.0 spec:
  // PM: All 8 reports
  // FC: Month End Inventory, Receiving Report, Variance Report, Spend by Department
  // DH: Spend by Department (their department only)
  // Storekeeper: Low Stock Report ONLY
  const canViewInventoryCount = isPM; // PM only
  const canViewMonthEnd = isPM || isFC; // PM, FC
  const canViewLowStock = isPM || isStorekeeper; // PM, Storekeeper
  const canViewReceiving = isPM || isFC; // PM, FC
  const canViewVariance = isPM || isFC; // PM, FC
  const canViewSpendByDept = isPM || isFC || isDH; // PM, FC, DH
  const canViewInternalReqs = isPM; // PM only
  const canViewOpenPRs = isPM || isDH; // PM and Department Head
  const canViewPOReports = isPM; // Open POs, Overdue, Vendor Performance - PM only
  // Landed Cost & Container Breakdown: PM by default; FC only when property setting is enabled
  const [fcCanViewLandedCost, setFcCanViewLandedCost] = useState(false);
  const canViewLandedCost = isPM || (isFC && fcCanViewLandedCost);
  
  // Set default tab based on user's role
  const getDefaultTab = () => {
    if (isStorekeeper) return 'low-stock'; // Storekeeper goes straight to Low Stock
    if (isDH) return 'spend-dept'; // DH goes to Spend by Dept
    if (canViewInventoryCount) return 'inventory-count';
    if (canViewMonthEnd) return 'month-end';
    if (canViewSpendByDept) return 'spend-dept';
    return 'spend-dept'; // Fallback
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  // Data states for reports
  const [inventoryCount, setInventoryCount] = useState({ items: [], total_items: 0, items_below_par: 0 });
  const [monthEndInventory, setMonthEndInventory] = useState({ items: [], total_items: 0, total_value: 0 });
  const [lowStock, setLowStock] = useState({ items: [], total_low_stock_items: 0 });
  const [receivingReport, setReceivingReport] = useState({ records: [], total_records: 0, total_value: 0 });
  const [varianceReport, setVarianceReport] = useState({ records: [], total_variances: 0 });
  const [departmentSpend, setDepartmentSpend] = useState([]);
  const [internalReqReport, setInternalReqReport] = useState({ requisitions: [], summary: {} });
  const [landedCostReport, setLandedCostReport] = useState({ shipments: [], shipment_count: 0, grand_total_purchase_value: 0, grand_total_landed_cost_charges: 0, grand_true_landed_cost: 0 });
  
  // Legacy data states (PM only)
  const [openPOs, setOpenPOs] = useState([]);
  const [openRequisitions, setOpenRequisitions] = useState([]);
  const [overduePOs, setOverduePOs] = useState([]);
  const [vendorPerformance, setVendorPerformance] = useState([]);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [departments] = useState(['Kitchen', 'Housekeeping', 'Front Office', 'F&B', 'Maintenance', 'Spa', 'Food and Beverage', 'Engineering']);
  
  // Date filters
  const [dateFilters, setDateFilters] = useState({
    start_date: '',
    end_date: ''
  });
  const [selectedPreset, setSelectedPreset] = useState('all');
  
  // Date preset options
  const DATE_PRESETS = [
    { value: 'all', label: 'All Time' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'last_3_months', label: 'Last 3 Months' },
    { value: 'year_to_date', label: 'Year to Date' },
    { value: 'custom', label: 'Custom Range' }
  ];
  
  // Helper to calculate date ranges from presets
  const applyDatePreset = (preset) => {
    setSelectedPreset(preset);
    const today = new Date();
    let startDate = '';
    let endDate = format(today, 'yyyy-MM-dd');
    
    switch (preset) {
      case 'this_month':
        startDate = format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd');
        break;
      case 'last_month':
        startDate = format(new Date(today.getFullYear(), today.getMonth() - 1, 1), 'yyyy-MM-dd');
        endDate = format(new Date(today.getFullYear(), today.getMonth(), 0), 'yyyy-MM-dd');
        break;
      case 'last_quarter':
        const quarterStart = Math.floor(today.getMonth() / 3) * 3;
        startDate = format(new Date(today.getFullYear(), quarterStart - 3, 1), 'yyyy-MM-dd');
        endDate = format(new Date(today.getFullYear(), quarterStart, 0), 'yyyy-MM-dd');
        break;
      case 'last_3_months':
        startDate = format(new Date(today.getFullYear(), today.getMonth() - 3, 1), 'yyyy-MM-dd');
        break;
      case 'year_to_date':
        startDate = format(new Date(today.getFullYear(), 0, 1), 'yyyy-MM-dd');
        break;
      case 'all':
        startDate = '';
        endDate = '';
        break;
      case 'custom':
        // Keep current custom dates
        return;
      default:
        startDate = '';
        endDate = '';
    }
    
    setDateFilters({ start_date: startDate, end_date: endDate });
  };

  // Get tab-specific storage key for session isolation
  const getTabStorageKey = () => {
    let tabId = window.__HERCHAIN_TAB_ID__;
    if (!tabId) {
      tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      window.__HERCHAIN_TAB_ID__ = tabId;
    }
    return `herchain_session_${tabId}`;
  };

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem(getTabStorageKey());
    return { Authorization: `Bearer ${token}` };
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/vendors`, { headers: getAuthHeaders() });
      setVendors(res.data);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

  // New report fetchers
  const fetchInventoryCount = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/inventory-count`, { headers: getAuthHeaders() });
      setInventoryCount(res.data);
    } catch (error) {
      console.error('Failed to fetch inventory count:', error);
    }
  };

  const fetchMonthEndInventory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/month-end-inventory`, { headers: getAuthHeaders() });
      setMonthEndInventory(res.data);
    } catch (error) {
      console.error('Failed to fetch month end inventory:', error);
    }
  };

  const fetchLowStock = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/low-stock`, { headers: getAuthHeaders() });
      setLowStock(res.data);
    } catch (error) {
      console.error('Failed to fetch low stock:', error);
    }
  };

  const fetchReceivingReport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFilters.start_date) params.append('start_date', dateFilters.start_date);
      if (dateFilters.end_date) params.append('end_date', dateFilters.end_date);
      const res = await axios.get(`${API_URL}/api/reports/receiving?${params}`, { headers: getAuthHeaders() });
      setReceivingReport(res.data);
    } catch (error) {
      console.error('Failed to fetch receiving report:', error);
    }
  };

  const fetchVarianceReport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFilters.start_date) params.append('start_date', dateFilters.start_date);
      if (dateFilters.end_date) params.append('end_date', dateFilters.end_date);
      const res = await axios.get(`${API_URL}/api/reports/variance?${params}`, { headers: getAuthHeaders() });
      setVarianceReport(res.data);
    } catch (error) {
      console.error('Failed to fetch variance report:', error);
    }
  };

  const fetchDepartmentSpend = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/spend-by-department`, { headers: getAuthHeaders() });
      setDepartmentSpend(res.data);
    } catch (error) {
      console.error('Failed to fetch department spend:', error);
    }
  };

  const fetchInternalReqReport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFilters.start_date) params.append('start_date', dateFilters.start_date);
      if (dateFilters.end_date) params.append('end_date', dateFilters.end_date);
      const res = await axios.get(`${API_URL}/api/reports/internal-requisitions?${params}`, { headers: getAuthHeaders() });
      setInternalReqReport(res.data);
    } catch (error) {
      console.error('Failed to fetch internal requisition report:', error);
    }
  };

  // Legacy report fetchers
  const fetchOpenPOs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/open-pos`, { headers: getAuthHeaders() });
      setOpenPOs(res.data);
    } catch (error) {
      console.error('Failed to fetch open POs:', error);
    }
  };

  const fetchOpenRequisitions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/open-requisitions`, { headers: getAuthHeaders() });
      setOpenRequisitions(res.data);
    } catch (error) {
      console.error('Failed to fetch open requisitions:', error);
    }
  };

  const fetchOverduePOs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/overdue-pos`, { headers: getAuthHeaders() });
      setOverduePOs(res.data);
    } catch (error) {
      console.error('Failed to fetch overdue POs:', error);
    }
  };

  const fetchVendorPerformance = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/vendor-performance`, { headers: getAuthHeaders() });
      setVendorPerformance(res.data);
    } catch (error) {
      console.error('Failed to fetch vendor performance:', error);
    }
  };

  const fetchLandedCostReport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFilters.start_date) params.append('start_date', dateFilters.start_date);
      if (dateFilters.end_date) params.append('end_date', dateFilters.end_date);
      const res = await axios.get(`${API_URL}/api/reports/landed-cost-and-container-breakdown?${params}`, { headers: getAuthHeaders() });
      setLandedCostReport(res.data);
    } catch (error) {
      // 403 expected when FC has no access
      if (error?.response?.status !== 403) {
        console.error('Failed to fetch landed cost report:', error);
      }
    }
  };

  const fetchFCLandedCostAccess = async () => {
    if (!isFC) return;
    try {
      const propertyId = (user?.property_ids && user.property_ids[0]) || 'grand-bay-resort';
      const res = await axios.get(`${API_URL}/api/property-settings/${propertyId}`, { headers: getAuthHeaders() });
      setFcCanViewLandedCost(!!res.data?.landed_cost_report_fc_access);
    } catch {
      setFcCanViewLandedCost(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchVendors(),
      fetchInventoryCount(),
      fetchMonthEndInventory(),
      fetchLowStock(),
      fetchReceivingReport(),
      fetchVarianceReport(),
      fetchDepartmentSpend(),
      fetchInternalReqReport(),
      fetchOpenPOs(),
      fetchOpenRequisitions(),
      fetchOverduePOs(),
      fetchVendorPerformance(),
      fetchFCLandedCostAccess(),
      fetchLandedCostReport()
    ]);
    setLastUpdated(new Date());
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // PDF Download function
  const downloadPdf = async (reportType, reportName) => {
    setDownloadingPdf(reportType);
    try {
      const params = new URLSearchParams();
      if (dateFilters.start_date) params.append('start_date', dateFilters.start_date);
      if (dateFilters.end_date) params.append('end_date', dateFilters.end_date);
      
      const reportPath = reportType === 'landed-cost-and-container-breakdown'
        ? 'landed-cost-and-container-breakdown'
        : reportType;
      const response = await axios.get(`${API_URL}/api/reports/${reportPath}/pdf?${params}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`${reportName} downloaded successfully`);
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error(`Failed to download ${reportName}`);
    } finally {
      setDownloadingPdf(null);
    }
  };

  const exportToCSV = (data, filename, headers) => {
    const rows = data.map(item => headers.map(h => item[h.key] ?? ''));
    const csvContent = [
      headers.map(h => h.label).join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('Report exported successfully');
  };

  // Calculate max YTD value for bar chart
  const maxYTDValue = Math.max(...departmentSpend.map(d => d.ytd_value), 1);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Reports
          </h1>
          <p className="text-slate-500">Real-time procurement analytics and insights</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            Last updated: {format(lastUpdated, 'MMM d, yyyy h:mm a')}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAllData}
            disabled={loading}
            data-testid="refresh-reports-btn"
          >
            {loading ? <Spinner size={16} className="animate-spin" /> : <ArrowsClockwise size={16} />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Range Filter - Global */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4" data-testid="date-filter-section">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Date Range:</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {DATE_PRESETS.map(preset => (
              <button
                key={preset.value}
                onClick={() => applyDatePreset(preset.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedPreset === preset.value
                    ? 'bg-[#0F52BA] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`preset-${preset.value}`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {selectedPreset === 'custom' && (
            <div className="flex items-center gap-2 ml-auto">
              <Input
                type="date"
                value={dateFilters.start_date}
                onChange={(e) => setDateFilters({ ...dateFilters, start_date: e.target.value })}
                className="w-36"
                data-testid="custom-start-date"
              />
              <span className="text-slate-400">to</span>
              <Input
                type="date"
                value={dateFilters.end_date}
                onChange={(e) => setDateFilters({ ...dateFilters, end_date: e.target.value })}
                className="w-36"
                data-testid="custom-end-date"
              />
              <Button 
                size="sm" 
                onClick={fetchAllData} 
                className="bg-[#0F52BA] hover:bg-[#0A3D8F]"
                data-testid="apply-custom-dates"
              >
                Apply
              </Button>
            </div>
          )}
        </div>
        {(dateFilters.start_date || dateFilters.end_date) && selectedPreset !== 'all' && (
          <p className="text-xs text-slate-500 mt-2">
            Showing data from {dateFilters.start_date ? format(new Date(dateFilters.start_date), 'MMM d, yyyy') : 'start'} to {dateFilters.end_date ? format(new Date(dateFilters.end_date), 'MMM d, yyyy') : 'now'}
          </p>
        )}
      </div>

      {/* Tabs — horizontal scroll on all viewports, no wrapping (consistent + clearly defined). */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="relative">
          <TabsList
            className="
              w-full bg-slate-100 p-1 rounded-xl gap-1
              flex flex-nowrap overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300
              snap-x snap-mandatory
            "
            style={{ scrollbarWidth: 'thin' }}
            data-testid="reports-tabs-list"
          >
          {canViewInventoryCount && (
            <TabsTrigger value="inventory-count" className="flex-shrink-0 whitespace-nowrap snap-start data-[state=active]:bg-white rounded-lg text-xs px-3 py-2" data-testid="tab-inventory-count">
              <Package size={14} className="mr-1.5" /> Inventory Count
            </TabsTrigger>
          )}
          {canViewMonthEnd && (
            <TabsTrigger value="month-end" className="flex-shrink-0 whitespace-nowrap snap-start data-[state=active]:bg-white rounded-lg text-xs px-3 py-2" data-testid="tab-month-end">
              <FileText size={14} className="mr-1.5" /> Month End
            </TabsTrigger>
          )}
          {canViewLowStock && (
            <TabsTrigger value="low-stock" className="flex-shrink-0 whitespace-nowrap snap-start data-[state=active]:bg-white rounded-lg text-xs px-3 py-2" data-testid="tab-low-stock">
              <WarningOctagon size={14} className="mr-1.5" /> Low Stock
            </TabsTrigger>
          )}
          {canViewReceiving && (
            <TabsTrigger value="receiving" className="flex-shrink-0 whitespace-nowrap snap-start data-[state=active]:bg-white rounded-lg text-xs px-3 py-2" data-testid="tab-receiving">
              <Receipt size={14} className="mr-1.5" /> Receiving
            </TabsTrigger>
          )}
          {canViewVariance && (
            <TabsTrigger value="variance" className="flex-shrink-0 whitespace-nowrap snap-start data-[state=active]:bg-white rounded-lg text-xs px-3 py-2" data-testid="tab-variance">
              <Scales size={14} className="mr-1.5" /> Variance
            </TabsTrigger>
          )}
          {canViewSpendByDept && (
            <TabsTrigger value="spend-dept" className="flex-shrink-0 whitespace-nowrap snap-start data-[state=active]:bg-white rounded-lg text-xs px-3 py-2" data-testid="tab-spend-dept">
              <CurrencyDollar size={14} className="mr-1.5" /> Spend by Dept
            </TabsTrigger>
          )}
          {canViewLandedCost && (
            <TabsTrigger value="landed-cost" className="flex-shrink-0 whitespace-nowrap snap-start data-[state=active]:bg-white rounded-lg text-xs px-3 py-2" data-testid="tab-landed-cost">
              <CurrencyDollar size={14} className="mr-1.5" /> Landed Cost & Container
            </TabsTrigger>
          )}
          {canViewInternalReqs && (
            <TabsTrigger value="internal-reqs" className="flex-shrink-0 whitespace-nowrap snap-start data-[state=active]:bg-white rounded-lg text-xs px-3 py-2" data-testid="tab-internal-reqs">
              <ClipboardText size={14} className="mr-1.5" /> Internal Reqs
            </TabsTrigger>
          )}
          {canViewOpenPRs && (
            <TabsTrigger value="open-prs" className="flex-shrink-0 whitespace-nowrap snap-start data-[state=active]:bg-white rounded-lg text-xs px-3 py-2" data-testid="tab-open-prs">
              <ClipboardText size={14} className="mr-1.5" /> Open PRs
            </TabsTrigger>
          )}
          {canViewPOReports && (
            <>
              <TabsTrigger value="open-pos" className="flex-shrink-0 whitespace-nowrap snap-start data-[state=active]:bg-white rounded-lg text-xs px-3 py-2">
                Open POs
              </TabsTrigger>
              <TabsTrigger value="overdue" className="flex-shrink-0 whitespace-nowrap snap-start data-[state=active]:bg-white rounded-lg text-xs px-3 py-2">
                Overdue POs
              </TabsTrigger>
              <TabsTrigger value="vendor-performance" className="flex-shrink-0 whitespace-nowrap snap-start data-[state=active]:bg-white rounded-lg text-xs px-3 py-2">
                Vendor Performance
              </TabsTrigger>
            </>
          )}
        </TabsList>
        </div>

        {/* ============ NEW REPORTS ============ */}

        {/* Tab: Inventory Count Report */}
        <TabsContent value="inventory-count" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="inventory-count-report">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#3D1D72]/10 flex items-center justify-center">
                  <Package size={20} weight="duotone" className="text-[#3D1D72]" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Inventory Count Report</h2>
                  <p className="text-xs text-slate-500">{inventoryCount.total_items} items tracked • {inventoryCount.items_below_par} below par</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => downloadPdf('inventory-count', 'Inventory_Count_Report')}
                  disabled={downloadingPdf === 'inventory-count'}
                  className="bg-[#3D1D72] hover:bg-[#2d1555]"
                  data-testid="download-inventory-count-pdf"
                >
                  {downloadingPdf === 'inventory-count' ? <Spinner size={16} className="animate-spin" /> : <FilePdf size={16} />}
                  Download PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(inventoryCount.items, 'inventory_count', [
                    { key: 'item_name', label: 'Item' },
                    { key: 'category', label: 'Category' },
                    { key: 'unit', label: 'Unit' },
                    { key: 'par_level', label: 'Par Level' },
                    { key: 'current_stock', label: 'Current Stock' }
                  ])}
                >
                  <Export size={16} /> Export CSV
                </Button>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 p-4 border-b border-slate-100 bg-slate-50">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#3D1D72]">{inventoryCount.total_items}</p>
                <p className="text-xs text-slate-500">Total Items</p>
              </div>
              <div className="text-center">
                <p className={`text-2xl font-bold ${inventoryCount.items_below_par > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {inventoryCount.items_below_par}
                </p>
                <p className="text-xs text-slate-500">Below Par Level</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {inventoryCount.total_items - inventoryCount.items_below_par}
                </p>
                <p className="text-xs text-slate-500">Adequate Stock</p>
              </div>
            </div>
            
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#3D1D72] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Item Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Unit</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Par Level</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Current Stock</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Variance</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                      <Spinner size={24} className="animate-spin mx-auto mb-2" />Loading...
                    </td></tr>
                  ) : inventoryCount.items.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No inventory items found</td></tr>
                  ) : (
                    inventoryCount.items.map((item, idx) => {
                      const variance = item.current_stock - item.par_level;
                      const isBelowPar = variance < 0;
                      return (
                        <tr key={idx} className={`hover:bg-slate-50 ${idx % 2 === 0 ? 'bg-[#F0EBF8]' : 'bg-white'}`}>
                          <td className="px-4 py-3 font-medium text-slate-800">{item.item_name}</td>
                          <td className="px-4 py-3 text-slate-600">{item.category}</td>
                          <td className="px-4 py-3 text-slate-600">{item.unit}</td>
                          <td className="px-4 py-3 text-center text-slate-600">{item.par_level}</td>
                          <td className="px-4 py-3 text-center font-medium text-slate-800">{item.current_stock}</td>
                          <td className={`px-4 py-3 text-center font-bold ${isBelowPar ? 'text-red-600' : 'text-green-600'}`}>
                            {variance > 0 ? '+' : ''}{variance}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              isBelowPar ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {isBelowPar ? 'Below Par' : 'OK'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Tab: Month End Inventory Report */}
        <TabsContent value="month-end" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="month-end-report">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#0F52BA]/10 flex items-center justify-center">
                  <FileText size={20} weight="duotone" className="text-[#0F52BA]" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Month End Inventory Report</h2>
                  <p className="text-xs text-slate-500">{monthEndInventory.total_items} items • Total Value: ${monthEndInventory.total_value?.toLocaleString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => downloadPdf('month-end-inventory', 'Month_End_Inventory_Report')}
                  disabled={downloadingPdf === 'month-end-inventory'}
                  className="bg-[#3D1D72] hover:bg-[#2d1555]"
                  data-testid="download-month-end-pdf"
                >
                  {downloadingPdf === 'month-end-inventory' ? <Spinner size={16} className="animate-spin" /> : <FilePdf size={16} />}
                  Download PDF
                </Button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#3D1D72] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Item Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Category</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Opening</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Received</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Issued</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Closing</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Unit Cost</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Total Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      <Spinner size={24} className="animate-spin mx-auto mb-2" />Loading...
                    </td></tr>
                  ) : (
                    monthEndInventory.items?.map((item, idx) => (
                      <tr key={idx} className={`hover:bg-slate-50 ${idx % 2 === 0 ? 'bg-[#F0EBF8]' : 'bg-white'}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{item.item_name}</td>
                        <td className="px-4 py-3 text-slate-600">{item.category}</td>
                        <td className="px-4 py-3 text-center text-slate-600">{item.opening_stock}</td>
                        <td className="px-4 py-3 text-center text-green-600">{item.received_qty}</td>
                        <td className="px-4 py-3 text-center text-red-600">{item.issued_qty}</td>
                        <td className="px-4 py-3 text-center font-medium text-slate-800">{item.closing_stock}</td>
                        <td className="px-4 py-3 text-right text-slate-600">${item.unit_cost?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right font-bold text-[#3D1D72]">${item.total_value?.toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {monthEndInventory.items?.length > 0 && (
                  <tfoot className="bg-[#F0EBF8]">
                    <tr>
                      <td colSpan={7} className="px-4 py-3 text-right font-bold text-slate-800">TOTAL:</td>
                      <td className="px-4 py-3 text-right font-bold text-[#3D1D72]">${monthEndInventory.total_value?.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Tab: Low Stock Report */}
        <TabsContent value="low-stock" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="low-stock-report">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <WarningOctagon size={20} weight="fill" className="text-red-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Low Stock Alert Report</h2>
                  <p className="text-xs text-slate-500">{lowStock.total_low_stock_items} items need reordering</p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => downloadPdf('low-stock', 'Low_Stock_Report')}
                disabled={downloadingPdf === 'low-stock'}
                className="bg-[#3D1D72] hover:bg-[#2d1555]"
                data-testid="download-low-stock-pdf"
              >
                {downloadingPdf === 'low-stock' ? <Spinner size={16} className="animate-spin" /> : <FilePdf size={16} />}
                Download PDF
              </Button>
            </div>

            {lowStock.total_low_stock_items > 0 && (
              <div className="p-3 bg-red-50 border-b border-red-100">
                <p className="text-red-700 font-medium text-center">
                  <Warning size={18} className="inline mr-2" />
                  {lowStock.total_low_stock_items} items require immediate attention
                </p>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#3D1D72] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Item Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Category</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Par Level</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Current Stock</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Shortfall</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Reorder Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                      <Spinner size={24} className="animate-spin mx-auto mb-2" />Loading...
                    </td></tr>
                  ) : lowStock.items?.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-green-600">
                      <CheckCircle size={32} className="mx-auto mb-2" />
                      All stock levels are adequate
                    </td></tr>
                  ) : (
                    lowStock.items?.map((item, idx) => (
                      <tr key={idx} className={`hover:bg-red-50 ${idx % 2 === 0 ? 'bg-[#F0EBF8]' : 'bg-white'}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{item.item_name}</td>
                        <td className="px-4 py-3 text-slate-600">{item.category}</td>
                        <td className="px-4 py-3 text-center text-slate-600">{item.par_level}</td>
                        <td className="px-4 py-3 text-center font-bold text-red-600">{item.current_stock}</td>
                        <td className="px-4 py-3 text-center font-bold text-red-600">{item.shortfall}</td>
                        <td className="px-4 py-3 text-center font-bold text-green-600">{item.reorder_qty}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Tab: Receiving Report */}
        <TabsContent value="receiving" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="receiving-report">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#2D6A4F]/10 flex items-center justify-center">
                  <Receipt size={20} weight="duotone" className="text-[#2D6A4F]" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Receiving Report</h2>
                  <p className="text-xs text-slate-500">{receivingReport.total_records} receipts • Total: ${receivingReport.total_value?.toLocaleString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
                  <Funnel size={16} /> Filters
                </Button>
                <Button
                  size="sm"
                  onClick={() => downloadPdf('receiving', 'Receiving_Report')}
                  disabled={downloadingPdf === 'receiving'}
                  className="bg-[#3D1D72] hover:bg-[#2d1555]"
                  data-testid="download-receiving-pdf"
                >
                  {downloadingPdf === 'receiving' ? <Spinner size={16} className="animate-spin" /> : <FilePdf size={16} />}
                  Download PDF
                </Button>
              </div>
            </div>

            {showFilters && activeTab === 'receiving' && (
              <div className="p-4 bg-slate-50 border-b border-slate-100">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">From Date</Label>
                    <Input type="date" value={dateFilters.start_date} onChange={(e) => setDateFilters({...dateFilters, start_date: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">To Date</Label>
                    <Input type="date" value={dateFilters.end_date} onChange={(e) => setDateFilters({...dateFilters, end_date: e.target.value})} />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button size="sm" onClick={fetchReceivingReport}>Apply</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setDateFilters({ start_date: '', end_date: '' }); fetchReceivingReport(); }}>
                      <X size={14} /> Clear
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#3D1D72] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">PO Number</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Received Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Received By</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Items</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Total Value</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                      <Spinner size={24} className="animate-spin mx-auto mb-2" />Loading...
                    </td></tr>
                  ) : receivingReport.records?.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No receiving records found</td></tr>
                  ) : (
                    receivingReport.records?.map((record, idx) => (
                      <tr key={idx} className={`hover:bg-slate-50 ${idx % 2 === 0 ? 'bg-[#F0EBF8]' : 'bg-white'}`}>
                        <td className="px-4 py-3 font-medium text-[#3D1D72]">{record.po_number}</td>
                        <td className="px-4 py-3 text-slate-600">{record.vendor_name}</td>
                        <td className="px-4 py-3 text-slate-600">{record.received_at ? format(new Date(record.received_at), 'MMM d, yyyy') : '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{record.received_by_name}</td>
                        <td className="px-4 py-3 text-center text-slate-600">{record.items_count}</td>
                        <td className="px-4 py-3 text-right font-medium text-slate-800">${record.total_value?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            record.has_variances ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {record.has_variances ? 'Variance' : 'Complete'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Tab: Variance Report */}
        <TabsContent value="variance" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="variance-report">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Scales size={20} weight="duotone" className="text-amber-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Variance Report</h2>
                  <p className="text-xs text-slate-500">{varianceReport.total_variances} variance records found</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
                  <Funnel size={16} /> Filters
                </Button>
                <Button
                  size="sm"
                  onClick={() => downloadPdf('variance', 'Variance_Report')}
                  disabled={downloadingPdf === 'variance'}
                  className="bg-[#3D1D72] hover:bg-[#2d1555]"
                  data-testid="download-variance-pdf"
                >
                  {downloadingPdf === 'variance' ? <Spinner size={16} className="animate-spin" /> : <FilePdf size={16} />}
                  Download PDF
                </Button>
              </div>
            </div>

            {showFilters && activeTab === 'variance' && (
              <div className="p-4 bg-slate-50 border-b border-slate-100">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">From Date</Label>
                    <Input type="date" value={dateFilters.start_date} onChange={(e) => setDateFilters({...dateFilters, start_date: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">To Date</Label>
                    <Input type="date" value={dateFilters.end_date} onChange={(e) => setDateFilters({...dateFilters, end_date: e.target.value})} />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button size="sm" onClick={fetchVarianceReport}>Apply</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setDateFilters({ start_date: '', end_date: '' }); fetchVarianceReport(); }}>
                      <X size={14} /> Clear
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#3D1D72] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">PO Number</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Item</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Ordered</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Received</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Variance</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Variance Note</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      <Spinner size={24} className="animate-spin mx-auto mb-2" />Loading...
                    </td></tr>
                  ) : varianceReport.records?.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-green-600">
                      <CheckCircle size={32} className="mx-auto mb-2" />
                      No variances recorded in the selected period
                    </td></tr>
                  ) : (
                    varianceReport.records?.map((record, idx) => (
                      <tr key={idx} className={`hover:bg-amber-50 ${idx % 2 === 0 ? 'bg-[#F0EBF8]' : 'bg-white'}`}>
                        <td className="px-4 py-3 font-medium text-[#3D1D72]">{record.po_number}</td>
                        <td className="px-4 py-3 text-slate-600">{record.vendor_name}</td>
                        <td className="px-4 py-3 text-slate-600">{record.item_name}</td>
                        <td className="px-4 py-3 text-center text-slate-600">{record.ordered_quantity}</td>
                        <td className="px-4 py-3 text-center text-slate-600">{record.received_quantity}</td>
                        <td className="px-4 py-3 text-center font-bold text-red-600">{record.variance}</td>
                        <td className="px-4 py-3 text-slate-600 text-sm max-w-[200px] truncate">{record.variance_note || '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{record.received_date}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Tab: Spend by Department */}
        <TabsContent value="spend-dept" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="spend-dept-report">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#2D6A4F]/10 flex items-center justify-center">
                  <CurrencyDollar size={20} weight="duotone" className="text-[#2D6A4F]" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Spend by Department</h2>
                  <p className="text-xs text-slate-500">{departmentSpend.length} departments tracked</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => downloadPdf('spend-by-department', 'Spend_by_Department_Report')}
                  disabled={downloadingPdf === 'spend-by-department'}
                  className="bg-[#3D1D72] hover:bg-[#2d1555]"
                  data-testid="download-spend-dept-pdf"
                >
                  {downloadingPdf === 'spend-by-department' ? <Spinner size={16} className="animate-spin" /> : <FilePdf size={16} />}
                  Download PDF
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(departmentSpend, 'department_spend_report', [
                    { key: 'department', label: 'Department' },
                    { key: 'current_month_pos', label: 'Month POs' },
                    { key: 'current_month_value', label: 'Month Value' },
                    { key: 'ytd_pos', label: 'YTD POs' },
                    { key: 'ytd_value', label: 'YTD Value' }
                  ])}
                >
                  <Export size={16} /> Export CSV
                </Button>
              </div>
            </div>
            
            {/* Bar Chart */}
            <div className="p-6 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Year-to-Date Spend by Department</h3>
              <div className="space-y-4">
                {departmentSpend.map(dept => (
                  <div key={dept.department} className="flex items-center gap-4">
                    <div className="w-32 text-sm font-medium text-slate-700 truncate">{dept.department}</div>
                    <div className="flex-1 h-8 bg-slate-100 rounded-lg overflow-hidden relative">
                      <div 
                        className="h-full bg-gradient-to-r from-[#3D1D72] to-[#5B2D9E] rounded-lg transition-all duration-500"
                        style={{ width: `${(dept.ytd_value / maxYTDValue) * 100}%` }}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-600">
                        ${dept.ytd_value.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#3D1D72] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Department</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase" colSpan={2}>Current Month</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase" colSpan={2}>Current Quarter</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase" colSpan={2}>Year to Date</th>
                  </tr>
                  <tr className="bg-[#3D1D72]/80">
                    <th className="px-4 py-1"></th>
                    <th className="px-4 py-1 text-xs font-medium text-white/80">POs</th>
                    <th className="px-4 py-1 text-xs font-medium text-white/80">Value</th>
                    <th className="px-4 py-1 text-xs font-medium text-white/80">POs</th>
                    <th className="px-4 py-1 text-xs font-medium text-white/80">Value</th>
                    <th className="px-4 py-1 text-xs font-medium text-white/80">POs</th>
                    <th className="px-4 py-1 text-xs font-medium text-white/80">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                      <Spinner size={24} className="animate-spin mx-auto mb-2" />Loading...
                    </td></tr>
                  ) : departmentSpend.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No department spend data available</td></tr>
                  ) : (
                    departmentSpend.map((dept, idx) => (
                      <tr key={dept.department} className={`hover:bg-slate-50 ${idx % 2 === 0 ? 'bg-[#F0EBF8]' : 'bg-white'}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{dept.department}</td>
                        <td className="px-4 py-3 text-center text-slate-600">{dept.current_month_pos}</td>
                        <td className="px-4 py-3 text-center text-slate-800">${dept.current_month_value.toLocaleString()}</td>
                        <td className="px-4 py-3 text-center text-slate-600">{dept.current_quarter_pos}</td>
                        <td className="px-4 py-3 text-center text-slate-800">${dept.current_quarter_value.toLocaleString()}</td>
                        <td className="px-4 py-3 text-center font-semibold text-slate-600">{dept.ytd_pos}</td>
                        <td className="px-4 py-3 text-center font-bold text-[#3D1D72]">${dept.ytd_value.toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Tab: Landed Cost & Container Breakdown Report */}
        <TabsContent value="landed-cost" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="landed-cost-report">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#3D1D72]/10 flex items-center justify-center">
                  <CurrencyDollar size={20} weight="duotone" className="text-[#3D1D72]" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Landed Cost & Container Breakdown</h2>
                  <p className="text-xs text-slate-500">
                    {landedCostReport.shipment_count || 0} shipments · {landedCostReport.shipments?.reduce((s, sh) => s + (sh.pos?.length || 0), 0) || 0} POs · Per-item allocation breakdown
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => downloadPdf('landed-cost-and-container-breakdown', 'Landed_Cost_Container_Breakdown')}
                disabled={downloadingPdf === 'landed-cost-and-container-breakdown'}
                className="bg-[#3D1D72] hover:bg-[#2d1555]"
                data-testid="download-landed-cost-pdf"
              >
                {downloadingPdf === 'landed-cost-and-container-breakdown' ? <Spinner size={16} className="animate-spin" /> : <FilePdf size={16} />}
                Download PDF
              </Button>
            </div>

            {/* Grand Totals */}
            <div className="grid grid-cols-3 gap-4 p-4 border-b border-slate-100 bg-slate-50">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#3D1D72]" data-testid="lc-grand-purchase-value">
                  ${(landedCostReport.grand_total_purchase_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-slate-500">Grand Total Purchase Value</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#3D1D72]" data-testid="lc-grand-charges">
                  ${(landedCostReport.grand_total_landed_cost_charges || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-slate-500">Total Landed Cost Charges</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#3D1D72]" data-testid="lc-grand-true-landed">
                  ${(landedCostReport.grand_true_landed_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-slate-500">Grand True Landed Cost</p>
              </div>
            </div>

            <div className="p-4 space-y-6">
              {(!landedCostReport.shipments || landedCostReport.shipments.length === 0) && (
                <p className="text-center text-slate-500 py-8" data-testid="lc-empty">
                  No landed cost postings in this date range.
                </p>
              )}
              {landedCostReport.shipments?.map((ship, sIdx) => (
                <div key={sIdx} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`shipment-${sIdx}`}>
                  <div className="p-3 bg-[#F0EBF8] flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-[#3D1D72]">
                        {ship.shipment_type}: {ship.shipment_reference}
                      </p>
                      <p className="text-xs text-slate-600">
                        Received {(ship.received_at || '').slice(0, 10) || '-'} ·
                        Freight Forwarder: {ship.freight_forwarder || '-'} ·
                        Allocation: {ship.allocation_method === 'by_volume' ? 'Volume / Weight' : 'Value'}
                      </p>
                    </div>
                    <div className="text-xs text-right text-slate-700">
                      <div>Freight ${(ship.shipment_total_freight || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · Trucking ${(ship.shipment_total_trucking || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      <div>Broker ${(ship.shipment_total_broker || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · Duty ${(ship.shipment_total_duty || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · Insurance ${(ship.shipment_total_insurance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      <div className="font-semibold text-[#3D1D72]">Total Charges: ${(ship.shipment_total_charges || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                  {ship.pos?.map((po, pIdx) => (
                    <div key={pIdx} className="border-t border-slate-100" data-testid={`shipment-${sIdx}-po-${pIdx}`}>
                      <div className="px-3 py-2 bg-slate-50 flex flex-wrap items-center justify-between gap-2">
                        <div className="text-sm">
                          <span className="font-semibold text-slate-800">{po.po_number}</span>
                          <span className="text-slate-500"> · {po.vendor_name}</span>
                          <span className="text-xs text-slate-500"> · Subtotal ${(po.po_subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="text-xs text-slate-600">
                          Allocated: F ${(po.po_allocated_freight || 0).toFixed(2)} · T ${(po.po_allocated_trucking || 0).toFixed(2)} · B ${(po.po_allocated_broker || 0).toFixed(2)} · D ${(po.po_allocated_duty || 0).toFixed(2)} · I ${(po.po_allocated_insurance || 0).toFixed(2)}
                          <span className="ml-2 font-semibold text-[#3D1D72]">Landed: ${(po.true_landed_cost || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-[#3D1D72] text-white">
                            <tr>
                              <th className="px-2 py-1 text-left">Item / SKU</th>
                              <th className="px-2 py-1 text-right">Qty</th>
                              <th className="px-2 py-1 text-right">Unit Price</th>
                              <th className="px-2 py-1 text-right">Purchase Value</th>
                              <th className="px-2 py-1 text-right">Freight</th>
                              <th className="px-2 py-1 text-right">Trucking</th>
                              <th className="px-2 py-1 text-right">Broker</th>
                              <th className="px-2 py-1 text-right">Duty</th>
                              <th className="px-2 py-1 text-right">Insurance</th>
                              <th className="px-2 py-1 text-right">Other</th>
                              <th className="px-2 py-1 text-right">Total Landed</th>
                              <th className="px-2 py-1 text-right">True Unit Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(po.items || []).map((it, iIdx) => (
                              <tr key={iIdx} className={iIdx % 2 ? 'bg-[#F0EBF8]/40' : 'bg-white'} data-testid={`shipment-${sIdx}-po-${pIdx}-item-${iIdx}`}>
                                <td className="px-2 py-1">
                                  <div className="font-medium text-slate-800">{it.item_name}</div>
                                  {it.sku && <div className="text-[10px] text-slate-500">SKU: {it.sku}</div>}
                                </td>
                                <td className="px-2 py-1 text-right">{it.received_quantity}</td>
                                <td className="px-2 py-1 text-right">${(it.unit_price || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(it.purchase_value || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(it.allocated_freight || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(it.allocated_trucking || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(it.allocated_broker || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(it.allocated_duty || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(it.allocated_insurance || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(it.allocated_other || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right font-semibold">${(it.total_landed_cost || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(it.true_unit_landed_cost || 0).toFixed(4)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>


        {/* Tab: Internal Requisitions Report */}
        <TabsContent value="internal-reqs" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="internal-reqs-report">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <ClipboardText size={20} weight="duotone" className="text-purple-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Internal Requisition Report</h2>
                  <p className="text-xs text-slate-500">{internalReqReport.requisitions?.length || 0} requisitions • {internalReqReport.summary?.total_items_issued || 0} items issued</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
                  <Funnel size={16} /> Filters
                </Button>
                <Button
                  size="sm"
                  onClick={() => downloadPdf('internal-requisitions', 'Internal_Requisition_Report')}
                  disabled={downloadingPdf === 'internal-requisitions'}
                  className="bg-[#3D1D72] hover:bg-[#2d1555]"
                  data-testid="download-internal-reqs-pdf"
                >
                  {downloadingPdf === 'internal-requisitions' ? <Spinner size={16} className="animate-spin" /> : <FilePdf size={16} />}
                  Download PDF
                </Button>
              </div>
            </div>

            {showFilters && activeTab === 'internal-reqs' && (
              <div className="p-4 bg-slate-50 border-b border-slate-100">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">From Date</Label>
                    <Input type="date" value={dateFilters.start_date} onChange={(e) => setDateFilters({...dateFilters, start_date: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">To Date</Label>
                    <Input type="date" value={dateFilters.end_date} onChange={(e) => setDateFilters({...dateFilters, end_date: e.target.value})} />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button size="sm" onClick={fetchInternalReqReport}>Apply</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setDateFilters({ start_date: '', end_date: '' }); fetchInternalReqReport(); }}>
                      <X size={14} /> Clear
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Department Summary */}
            {internalReqReport.summary?.by_department && Object.keys(internalReqReport.summary.by_department).length > 0 && (
              <div className="p-4 border-b border-slate-100 bg-slate-50">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Department Consumption Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(internalReqReport.summary.by_department).map(([dept, data]) => (
                    <div key={dept} className="bg-white p-3 rounded-lg border border-slate-200">
                      <p className="font-medium text-slate-800">{dept}</p>
                      <p className="text-sm text-slate-500">{data.total_requisitions} reqs • {data.total_items_issued} items</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#3D1D72] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Req #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Department</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Requester</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Items</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Issued By</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                      <Spinner size={24} className="animate-spin mx-auto mb-2" />Loading...
                    </td></tr>
                  ) : internalReqReport.requisitions?.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No internal requisitions found</td></tr>
                  ) : (
                    internalReqReport.requisitions?.map((req, idx) => (
                      <tr key={req.id} className={`hover:bg-slate-50 ${idx % 2 === 0 ? 'bg-[#F0EBF8]' : 'bg-white'}`}>
                        <td className="px-4 py-3 font-medium text-[#3D1D72]">{req.requisition_number}</td>
                        <td className="px-4 py-3 text-slate-600">{req.created_at ? format(new Date(req.created_at), 'MMM d, yyyy') : '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{req.department}</td>
                        <td className="px-4 py-3 text-slate-600">{req.requester_name}</td>
                        <td className="px-4 py-3 text-slate-600">{req.items?.length || 0} item(s)</td>
                        <td className="px-4 py-3 text-slate-600">{req.issued_by_name || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            req.status === 'fulfilled' ? 'bg-green-100 text-green-700' :
                            req.status === 'partially_fulfilled' ? 'bg-amber-100 text-amber-700' :
                            req.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                            req.status === 'in_progress' ? 'bg-purple-100 text-purple-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {req.status === 'fulfilled' ? 'Fulfilled' :
                             req.status === 'partially_fulfilled' ? 'Partial' :
                             req.status === 'submitted' ? 'Pending' :
                             req.status === 'in_progress' ? 'In Progress' : 'Rejected'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ============ LEGACY REPORTS ============ */}

        {/* Tab: Open PRs — PRs still awaiting conversion to PO */}
        <TabsContent value="open-prs" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="open-prs-panel">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#3D1D72]/10 flex items-center justify-center">
                  <ClipboardText size={20} weight="duotone" className="text-[#3D1D72]" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Open Purchase Requisitions</h2>
                  <p className="text-xs text-slate-500">
                    {openRequisitions.length} open {openRequisitions.length === 1 ? 'PR' : 'PRs'} awaiting conversion to PO
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!openRequisitions.length) {
                    toast.info('No open PRs to export');
                    return;
                  }
                  exportToCSV(openRequisitions, 'open_prs_report', [
                    { key: 'requisition_number', label: 'PR Number' },
                    { key: 'department_name', label: 'Department' },
                    { key: 'requester_name', label: 'Requester' },
                    { key: 'items_requested', label: 'Items' },
                    { key: 'estimated_value', label: 'Estimated Value' },
                    { key: 'date_submitted', label: 'Submitted' },
                    { key: 'priority', label: 'Priority' },
                    { key: 'status', label: 'Status' },
                  ]);
                }}
                data-testid="export-open-prs-btn"
              >
                <Export size={16} /> Export CSV
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="open-prs-table">
                <thead className="bg-[#3D1D72] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">PR Number</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Department</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Requester</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Items</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Est. Value</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Submitted</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">View</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">
                      <Spinner size={24} className="animate-spin mx-auto mb-2" />Loading...
                    </td></tr>
                  ) : openRequisitions.length === 0 ? (
                    <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">
                      No open purchase requisitions — all PRs have been converted or closed.
                    </td></tr>
                  ) : (
                    openRequisitions.map((pr, idx) => {
                      const statusStyle = {
                        pending_conversion: 'bg-amber-100 text-amber-700',
                        approved: 'bg-blue-100 text-blue-700',
                        returned: 'bg-orange-100 text-orange-700',
                      }[pr.status] || 'bg-slate-100 text-slate-600';
                      const statusLabel = {
                        pending_conversion: 'Pending Conversion',
                        approved: 'Approved',
                        returned: 'Returned — Awaiting Resubmit',
                      }[pr.status] || pr.status;
                      const priorityStyle = {
                        critical: 'bg-red-100 text-red-700',
                        high: 'bg-orange-100 text-orange-700',
                        medium: 'bg-blue-100 text-blue-700',
                        low: 'bg-slate-100 text-slate-600',
                      }[pr.priority] || 'bg-slate-100 text-slate-600';
                      return (
                        <tr key={pr.id} className={`hover:bg-slate-50 ${idx % 2 === 0 ? 'bg-[#F0EBF8]' : 'bg-white'}`} data-testid={`open-pr-row-${pr.id}`}>
                          <td className="px-4 py-3 font-medium text-[#3D1D72]">{pr.requisition_number}</td>
                          <td className="px-4 py-3 text-slate-600">{pr.department_name}</td>
                          <td className="px-4 py-3 text-slate-600">{pr.requester_name}</td>
                          <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{pr.items_requested}</td>
                          <td className="px-4 py-3 text-right font-medium text-slate-800">
                            ${(pr.estimated_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{format(new Date(pr.date_submitted), 'MMM d, yyyy')}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${priorityStyle}`}>
                              {pr.priority}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyle}`}>{statusLabel}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-[#3D1D72] hover:bg-[#3D1D72]/10"
                              onClick={() => navigate(`/requisitions?selected=${pr.id}`)}
                              data-testid={`view-pr-${pr.id}`}
                              title="View PR detail"
                            >
                              <Eye size={16} weight="duotone" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Tab: Open POs */}
        <TabsContent value="open-pos" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#3D1D72]/10 flex items-center justify-center">
                  <FileText size={20} weight="duotone" className="text-[#3D1D72]" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Open Purchase Orders</h2>
                  <p className="text-xs text-slate-500">{openPOs.length} active orders</p>
                </div>
              </div>
              <Button 
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!openPOs.length) { toast.info('No open POs to export'); return; }
                  exportToCSV(openPOs, 'open_pos_report', [
                    { key: 'po_number', label: 'PO Number' },
                    { key: 'vendor_name', label: 'Vendor' },
                    { key: 'department', label: 'Department' },
                    { key: 'total_value', label: 'Value' },
                    { key: 'currency', label: 'Currency' },
                    { key: 'required_delivery_date', label: 'Required Delivery' },
                    { key: 'status', label: 'Status' }
                  ]);
                }}
                data-testid="export-open-pos-btn"
              >
                <Export size={16} /> Export CSV
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#3D1D72] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">PO Number</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Department</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Value</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Required Delivery</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">View</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                      <Spinner size={24} className="animate-spin mx-auto mb-2" />Loading...
                    </td></tr>
                  ) : openPOs.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No open purchase orders</td></tr>
                  ) : (
                    openPOs.map((po, idx) => (
                      <tr key={po.id} className={`hover:bg-slate-50 ${idx % 2 === 0 ? 'bg-[#F0EBF8]' : 'bg-white'}`} data-testid={`open-po-row-${po.id}`}>
                        <td className="px-4 py-3 font-medium text-[#3D1D72]">{po.po_number}</td>
                        <td className="px-4 py-3 text-slate-600">{po.vendor_name}</td>
                        <td className="px-4 py-3 text-slate-600">{po.department || '-'}</td>
                        <td className="px-4 py-3 font-medium text-slate-800">{po.currency} {po.total_value.toLocaleString()}</td>
                        <td className="px-4 py-3 text-slate-600">{format(new Date(po.required_delivery_date), 'MMM d, yyyy')}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            po.status === 'issued' ? 'bg-blue-100 text-blue-700' :
                            po.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                            po.status === 'in_transit' ? 'bg-purple-100 text-purple-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>{po.status}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-[#3D1D72] hover:bg-[#3D1D72]/10"
                            onClick={() => navigate(`/purchase-orders/${po.id}`)}
                            data-testid={`view-po-${po.id}`}
                            title="View PO detail"
                          >
                            <Eye size={16} weight="duotone" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Tab: Overdue POs */}
        <TabsContent value="overdue" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#E63946]/10 flex items-center justify-center">
                  <Warning size={20} weight="fill" className="text-[#E63946]" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Overdue Purchase Orders</h2>
                  <p className="text-xs text-slate-500">{overduePOs.length} orders requiring attention</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!overduePOs.length) { toast.info('No overdue POs to export'); return; }
                  exportToCSV(overduePOs, 'overdue_pos_report', [
                    { key: 'po_number', label: 'PO Number' },
                    { key: 'vendor_name', label: 'Vendor' },
                    { key: 'total_value', label: 'Value' },
                    { key: 'currency', label: 'Currency' },
                    { key: 'stage_overdue', label: 'Stage Overdue' },
                    { key: 'days_overdue', label: 'Days Overdue' },
                    { key: 'currently_with_name', label: 'Currently With' },
                    { key: 'currently_with_role', label: 'Role' },
                  ]);
                }}
                data-testid="export-overdue-pos-btn"
              >
                <Export size={16} /> Export CSV
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#3D1D72] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">PO Number</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Total Value</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Stage Overdue</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Days Overdue</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">View</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                      <Spinner size={24} className="animate-spin mx-auto mb-2" />Loading...
                    </td></tr>
                  ) : overduePOs.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-green-600">
                      <CheckCircle size={32} className="mx-auto mb-2" />
                      No overdue orders
                    </td></tr>
                  ) : (
                    overduePOs.map((po, idx) => (
                      <tr key={po.id} className={`hover:bg-red-50 ${idx % 2 === 0 ? 'bg-[#F0EBF8]' : 'bg-white'}`} data-testid={`overdue-po-row-${po.id}`}>
                        <td className="px-4 py-3 font-medium text-[#E63946]">{po.po_number}</td>
                        <td className="px-4 py-3 text-slate-600">{po.vendor_name}</td>
                        <td className="px-4 py-3 font-medium text-slate-800">{po.currency} {po.total_value.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#E63946]/10 text-[#E63946]">
                            {po.stage_overdue}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-bold text-[#E63946]">{po.days_overdue} days</td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-[#3D1D72] hover:bg-[#3D1D72]/10"
                            onClick={() => navigate(`/purchase-orders/${po.id}`)}
                            data-testid={`view-overdue-po-${po.id}`}
                            title="View PO detail"
                          >
                            <Eye size={16} weight="duotone" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Tab: Vendor Performance */}
        <TabsContent value="vendor-performance" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#0F52BA]/10 flex items-center justify-center">
                  <Storefront size={20} weight="duotone" className="text-[#0F52BA]" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Vendor Performance</h2>
                  <p className="text-xs text-slate-500">{vendorPerformance.length} vendors tracked</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!vendorPerformance.length) { toast.info('No vendor data to export'); return; }
                  exportToCSV(vendorPerformance, 'vendor_performance_report', [
                    { key: 'vendor_name', label: 'Vendor' },
                    { key: 'total_pos_issued', label: 'Total POs' },
                    { key: 'total_value', label: 'Total Value' },
                    { key: 'avg_confirmation_days', label: 'Avg Confirmation (days)' },
                    { key: 'late_confirmations', label: 'Late Confirmations' },
                    { key: 'document_delays', label: 'Document Delays' },
                  ]);
                }}
                data-testid="export-vendor-perf-btn"
              >
                <Export size={16} /> Export CSV
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#3D1D72] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Total POs</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Total Value</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Avg Confirmation</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Late Confirmations</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Document Delays</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                      <Spinner size={24} className="animate-spin mx-auto mb-2" />Loading...
                    </td></tr>
                  ) : vendorPerformance.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No vendor data available</td></tr>
                  ) : (
                    vendorPerformance.map((vendor, idx) => (
                      <tr key={vendor.vendor_id} className={`hover:bg-slate-50 ${idx % 2 === 0 ? 'bg-[#F0EBF8]' : 'bg-white'}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{vendor.vendor_name}</td>
                        <td className="px-4 py-3 text-slate-600">{vendor.total_pos_issued}</td>
                        <td className="px-4 py-3 font-medium text-slate-800">${vendor.total_value.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <span className={`font-medium ${vendor.avg_confirmation_days > 2 ? 'text-[#E63946]' : 'text-[#2D6A4F]'}`}>
                            {vendor.avg_confirmation_days} days
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${vendor.late_confirmations > 0 ? 'bg-[#E63946]/10 text-[#E63946]' : 'bg-[#2D6A4F]/10 text-[#2D6A4F]'}`}>
                            {vendor.late_confirmations}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${vendor.document_delays > 0 ? 'bg-[#F4A261]/10 text-[#B45309]' : 'bg-[#2D6A4F]/10 text-[#2D6A4F]'}`}>
                            {vendor.document_delays}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
